import { type TerminalResource, TerminalsController_revoke_v1 } from '@repo/api-client';
import { Alert, AlertDescription } from '@repo/ui/components/alert';
import { Button } from '@repo/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/ui/components/dialog';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useState } from 'react';

import { useApiClient } from '@/api/api-client';
import { messageForFailure } from '@/api/failure';
import { IDEMPOTENCY_KEY_HEADER, newIdempotencyKey } from '@/lib/idempotency-key';

import { stateOf } from './terminal-states';

/**
 * Withdrawing a device: `POST /v1/terminals/{id}/revoke`.
 *
 * **The confirmation is the feature.** Revoking is one call with no options, so
 * everything this screen can get wrong is in the wording — and the wrong wording
 * here is the ordinary one. "Revoked" reads as "off right now", and it is not:
 * the API refuses the device at its next online request and refuses every
 * renewal after that, so a device *kept offline* goes on selling until its lease
 * runs out, which is at most 72 hours. Nothing is sent to the device, because
 * the case this exists for is one deliberately off the network. An owner acting
 * on a stolen till is deciding what else to do — call somebody, close the gate —
 * and that decision is made on this sentence.
 *
 * **There is no undo, and it is not hedged.** A device that comes back is a new
 * registration under a new terminal and a new prefix; the row here stays revoked
 * for good, because the receipts it issued are still out in the park and the
 * prefix on them must never mean two devices.
 *
 * **A terminal that was never registered gets different words.** It has no lease
 * to run out and no device using it, so the 72-hour sentence would be a promise
 * about something that is not happening. Saying it anyway would be this screen
 * inventing a fact, which is the one thing refusal copy must never do.
 */

export function RevokeTerminalDialog({
  terminal,
  onClose,
  onRevoked,
}: {
  /** The terminal being withdrawn, or `null` when none is. */
  terminal: TerminalResource | null;

  onClose: () => void;
  onRevoked: () => void;
}) {
  return (
    <Dialog
      open={terminal !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent>
        {/* A child for the reason the register dialog gives: closing unmounts
            it, so the next open carries a fresh idempotency key rather than
            replaying the answer to the last revocation. */}
        {terminal !== null && (
          <RevokeTerminalBody terminal={terminal} onRevoked={onRevoked} onClose={onClose} />
        )}
      </DialogContent>
    </Dialog>
  );
}

function RevokeTerminalBody({
  terminal,
  onClose,
  onRevoked,
}: {
  terminal: TerminalResource;
  onClose: () => void;
  onRevoked: () => void;
}) {
  const client = useApiClient();

  const [idempotencyKey] = useState(newIdempotencyKey);
  const [failure, setFailure] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  // Never a device, so there is no lease running and no window to state.
  const neverRegistered = stateOf(terminal) === 'AWAITING_REGISTRATION';

  const revoke = async () => {
    if (pending) return;

    setFailure(null);
    setPending(true);

    try {
      await client.request(TerminalsController_revoke_v1, {
        path: { id: terminal.id },
        headers: { [IDEMPOTENCY_KEY_HEADER]: idempotencyKey },
      });

      onRevoked();
    } catch (error) {
      setFailure(messageForFailure(error));
    } finally {
      setPending(false);
    }
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>Revoke {terminal.name}</DialogTitle>
        <DialogDescription>
          <span className="font-mono text-foreground numeric">{terminal.prefix}</span> is withdrawn
          from the park for good. This cannot be undone.
        </DialogDescription>
      </DialogHeader>

      {/* `text-foreground` on `bg-muted` — the muted-on-muted pairing misses AA
          and is the one a quiet inset panel invites. */}
      <div className="space-y-3 rounded-2xl bg-muted px-4 py-3 text-sm text-foreground">
        {neverRegistered ? (
          <p>
            This terminal has never been registered, so no device holds a lease for it and nothing
            is running that revoking has to stop. The row is closed, and its prefix stays taken for
            all time.
          </p>
        ) : (
          <p>
            Revoking stops the lease renewing. If the device is online it is refused at once. If it
            is offline it keeps working until its lease runs out — at most 72 hours — and nothing
            can be sent to it to stop it sooner.
          </p>
        )}

        <p>
          Other terminals at this outlet keep working. The prefix is never released, and a device
          that comes back is registered again as a new terminal under a new prefix rather than being
          un-revoked.
        </p>
      </div>

      {failure !== null && (
        <Alert variant="destructive">
          <IconAlertTriangle />
          <AlertDescription>{failure}</AlertDescription>
        </Alert>
      )}

      <DialogFooter>
        <Button variant="outline" onClick={onClose} disabled={pending}>
          Cancel
        </Button>
        {/* `destructive`, which this system reserves for the irreversible. */}
        <Button variant="destructive" onClick={() => void revoke()} disabled={pending}>
          {pending ? 'Revoking…' : 'Revoke terminal'}
        </Button>
      </DialogFooter>
    </>
  );
}
